/**
 * "Review outdated knowledge" (FRONT-2062) — `GET`/`POST /api/knowledge-review*`
 * (`src/stale-review/routes.ts`). Wire types below mirror
 * `src/stale-review/candidates.ts`'s `StaleReviewItem`/`StaleReviewPage` and
 * `src/stale-review/draft.ts`'s `PublicSuggestion` 1:1 — `Date` fields cross
 * the wire as ISO strings, same convention as `use-knowledge-catalogue.ts`.
 *
 * Key factory uses the newer, local (not `~/lib/query-keys`) convention —
 * see `use-knowledge-enrichment.ts` for the sibling example this follows.
 *
 * ── Drafting is polled, not pushed ──────────────────────────────────────
 * There is no SSE event for a draft finishing (same reasoning as smart
 * tagging's status hook) — `useKnowledgeReviewSuggestion` polls
 * `GET /api/knowledge-review/suggestions/:id` every 2s while, and only
 * while, the last-read `status` is `"drafting"`. Once the server reports
 * `ready`/`failed`/`applied`/`dismissed`, polling stops on its own — no
 * separate "stop" call needed (there is a real terminal status here; the
 * smart-tagging card instead counts finished passes from the database and
 * freezes its run when they reach the queued total).
 *
 * ── After an apply, every stale count on screen must agree ─────────────
 * Accepting/deleting/keeping an item can change: the review list itself,
 * the Home feed's `knowledgeReview` card count, the sidebar's Knowledge
 * badge (`/api/attention`), the OKF tab's "may be out
 * of date" figure (the catalogue overview), the plain file/document lists
 * (a delete moves the row to the recycle bin; an update changes its
 * content), and the faceted knowledge-search listings/facet counts (the
 * same corpus, filtered by freshness). `useApplyKnowledgeReviewAction`
 * invalidates all six so no number is left stale by the very action that
 * fixed staleness.
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { attentionKeys, feedKeys, filesOverviewKeys } from '~/lib/query-keys';
import { catalogueKeys } from './use-knowledge-catalogue';
import { documentKeys } from './use-documents';
import { fileKeys } from './use-files';
import { knowledgeSearchKeys } from './use-knowledge-search';
import { artifactChangeKeys } from './use-artifact-changes';

export type StaleReviewTargetKind = 'document' | 'upload';

/** Mirrors `SuggestionStatus` (`src/db/schema/knowledge-update-suggestions.ts`). */
export type ReviewSuggestionStatus = 'drafting' | 'ready' | 'failed' | 'applied' | 'dismissed';

/** Mirrors `SuggestionSource` (same schema file). */
export interface ReviewSuggestionSource {
  title: string;
  url?: string;
  kind: 'web' | 'knowledge';
  ref?: string;
  /** FRONT-2062 F18: which of the person's own corpora `ref` names — set
   *  server-side from the retrieval hit that produced this citation, not
   *  guessed from `ref`'s shape. `undefined` for a suggestion drafted
   *  before this field existed. */
  targetKind?: 'document' | 'upload';
}

/** Mirrors the list item's `suggestion` ref (`src/stale-review/candidates.ts`),
 *  FRONT-2123: `queued`/`startedAt`/`changed` let a row describe its own
 *  draft's lifecycle (Queued / Updating… / Ready to review / Nothing newer
 *  found) without fetching the full `PublicSuggestion`. */
export interface StaleReviewSuggestionRef {
  id: number;
  status: ReviewSuggestionStatus;
  /** FRONT-2123 R2: true while `status === 'drafting'` and the background
   *  job hasn't actually started work on this row yet — NOT simply
   *  `startedAt === null`. A job that starts running in the same tick a
   *  response is built can read `queued: false` with `startedAt` still
   *  `null`, before its own claim has landed and stamped `startedAt` —
   *  see the mirrored `PublicSuggestion.queued` below and its backend
   *  twin in `src/stale-review/draft.ts`. */
  queued: boolean;
  createdAt: string;
  /** ISO time the background job began work — null while queued, and for
   *  every non-drafting row that never started. */
  startedAt: string | null;
  /** Only meaningful once `status === 'ready'`. */
  changed: boolean | null;
}

/** Mirrors `StaleReviewItem` (`src/stale-review/candidates.ts`). */
export interface StaleReviewItem {
  kind: StaleReviewTargetKind;
  id: string;
  filename: string;
  title: string;
  okfType: string | null;
  updatedAt: string;
  staleSince: string;
  canUpdate: boolean;
  updateBlockedReason: string | null;
  suggestion: StaleReviewSuggestionRef | null;
  /** FRONT-2497: set when reflection flagged this project file because a
   *  chat contradicted it. Optional: absent on responses from before it. */
  flag?: StaleReviewFlag | null;
}

/** Mirrors `StaleReviewFlag` (`src/stale-review/candidates.ts`). */
export interface StaleReviewFlag {
  reason: string;
  projectName: string;
  /** The chat it came from, or null when that could not be verified. */
  sourceSessionId: string | null;
  flaggedAt: string;
}

export interface StaleReviewPage {
  total: number;
  items: StaleReviewItem[];
}

/** FRONT-3313: one proposed passage change (`src/stale-review/passage-edits.ts`). */
export interface PassageChange {
  id: string;
  oldText: string;
  newText: string;
  why: string | null;
}

/** FRONT-3313: what the person decided per change; a missing id means accept. */
export type PassageDecision =
  | { id: string; decision: 'accept' }
  | { id: string; decision: 'reject' }
  | { id: string; decision: 'edit'; text: string };

/** Mirrors `PublicSuggestion` (`src/stale-review/draft.ts`). */
export interface PublicSuggestion {
  id: number;
  status: ReviewSuggestionStatus;
  /** FRONT-2123 R2: true while `status === 'drafting'` and the row hasn't
   *  actually started work yet — NOT simply `startedAt === null`. A job
   *  that starts running in the same tick a response is built can read
   *  `queued: false` with `startedAt` still `null`, before its own claim
   *  has landed and stamped `startedAt`. A running row is always
   *  `status: 'drafting', queued: false`, but `startedAt` on it can still
   *  be `null` for a moment. */
  queued: boolean;
  /** ISO time the background job actually began work on this row — null
   *  while queued, and for every non-drafting row that never started. */
  startedAt: string | null;
  changed: boolean | null;
  changes: string[] | null;
  proposedContent: string | null;
  /** `null` for a passage-format draft: the cards carry the old text, the full document is not sent. */
  originalContent: string | null;
  sources: ReviewSuggestionSource[] | null;
  error: string | null;
  createdAt: string;
  /** FRONT-3313: the proposed passage changes, or `null` for an old-format
   *  draft (a full rewrite in `proposedContent`). */
  passageChanges: PassageChange[] | null;
  /** FRONT-3313: proposals EDDY made that could not be placed exactly once. */
  droppedChanges: number;
  /** FRONT-3313: true when this applied update can still be undone. */
  undoable: boolean;
}

export type ReviewAction = 'update' | 'delete' | 'keep';

export const knowledgeReviewKeys = {
  all: ['knowledge-review'] as const,
  lists: () => [...knowledgeReviewKeys.all, 'list'] as const,
  list: (limit: number, offset: number) => [...knowledgeReviewKeys.lists(), limit, offset] as const,
  suggestion: (id: number) => [...knowledgeReviewKeys.all, 'suggestion', id] as const,
} as const;

const DEFAULT_LIST_LIMIT = 50;

/**
 * FRONT-2062 F19: the review screen's own list, real offset-based paging —
 * `useInfiniteQuery` with a numeric `offset` page param, same shape as
 * `use-knowledge-search.ts`'s `useKnowledgeSearch` (that one pages by an
 * opaque cursor; this one by a plain integer, since the server route takes
 * `limit`/`offset` directly, not a cursor token). `getNextPageParam` sums
 * every page's `items.length` fetched SO FAR against the last page's own
 * `total` — never assumes a fixed page size, so a `total` that changes
 * between fetches (an item resolved elsewhere mid-session) still stops
 * paging at the right point rather than requesting an empty page forever
 * or stopping one page short.
 *
 * The query key is the STABLE series key (`lists()`), not `list(limit,
 * offset)` — `offset` is TanStack's own `pageParam` machinery, deliberately
 * outside the key, exactly like `useKnowledgeSearch`'s cursor. `limit`
 * genuinely never varies per caller today (`DEFAULT_LIST_LIMIT`, always),
 * so this can't silently share a cache entry across two different limits
 * the FRONT-1501 rule guards against — `list(limit, offset)` stays defined
 * below for the id-scoped `suggestion()` key's sibling tests, and in case a
 * future caller needs a single non-paginated page.
 *
 * `opts.enabled` (default `true`, same convention as `useKnowledgeReviewSuggestion`
 * below) lets the caller hold off entirely — FRONT-2062 F1: `KnowledgeReviewDialog`
 * is always mounted, so without this the list (and everything it drives —
 * auto-selecting an item) would fire the moment the panel mounts, well
 * before the person ever opens it.
 *
 * ── FRONT-2123: the list polls itself while anything loaded is drafting ──
 * Drafting no longer requires an item to be open (each row gets its own
 * Update button and can queue a draft without ever being selected), so
 * nothing else would tell an unopened row when its "Queued" turns into
 * "Updating…" or "Ready to review". `shouldPollReviewList` below is the
 * pure decision `refetchInterval` reads; TanStack already pauses the whole
 * query while `enabled: false` (the dialog closed), so this never needs to
 * check `open` itself.
 */
export function useKnowledgeReviewList(opts: { enabled?: boolean } = {}) {
  return useInfiniteQuery({
    queryKey: knowledgeReviewKeys.lists(),
    queryFn: ({ pageParam }) =>
      api.get<StaleReviewPage>(`/api/knowledge-review?limit=${DEFAULT_LIST_LIMIT}&offset=${pageParam}`),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    enabled: opts.enabled ?? true,
    refetchInterval: (q) => {
      const items = q.state.data?.pages.flatMap((p) => p.items) ?? [];
      return shouldPollReviewList(items) ? REVIEW_LIST_POLL_MS : false;
    },
  });
}

const REVIEW_LIST_POLL_MS = 3000;

/**
 * FRONT-2123 — whether the review list should keep polling: true iff at
 * least one item ALREADY LOADED has an active draft. Queued and running
 * both read as `status: 'drafting'` on the wire (see the module doc
 * comment and the shared FRONT-2123 contract) — either one is reason
 * enough to keep polling, since either can flip to `ready`/`failed` at any
 * moment without the person ever opening that row.
 */
export function shouldPollReviewList(items: Array<Pick<StaleReviewItem, 'suggestion'>>): boolean {
  return items.some((it) => it.suggestion?.status === 'drafting');
}

/**
 * Read one draft back, polling every 2s while (and only while) it is still
 * `drafting`. `enabled` lets the caller hold off until a suggestion id
 * actually exists (e.g. before the start-draft mutation has resolved).
 */
export function useKnowledgeReviewSuggestion(id: number | null | undefined, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: knowledgeReviewKeys.suggestion(id ?? -1),
    queryFn: () => api.get<PublicSuggestion>(`/api/knowledge-review/suggestions/${id}`),
    enabled: id != null && (opts.enabled ?? true),
    refetchInterval: (q) => (q.state.data?.status === 'drafting' ? 2000 : false),
  });
}

/**
 * Start (or reuse an already-active) draft for one item. The response is
 * seeded straight into the suggestion's own query-cache entry so the detail
 * pane can render it immediately, with no extra round trip before
 * `useKnowledgeReviewSuggestion`'s own poll takes over.
 *
 * FRONT-2062 C4: `force` (omitted, i.e. `undefined`, on a normal first
 * start — `JSON.stringify` drops the key entirely, so the request body is
 * byte-identical to before this existed) tells the backend to skip its
 * own reuse-an-active-suggestion dedupe and start a genuinely new draft.
 * Every "Try again"/"Draft again" retry in `knowledge-review-dialog.tsx`
 * passes `force: true` — see that file's `startOrRetryDraft`.
 */
export function useStartKnowledgeReviewDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, id, force }: { kind: StaleReviewTargetKind; id: string; force?: boolean }) =>
      api.post<PublicSuggestion>('/api/knowledge-review/suggestions', { kind, id, force }),
    onSuccess: (suggestion) => {
      qc.setQueryData(knowledgeReviewKeys.suggestion(suggestion.id), suggestion);
    },
  });
}

export interface ApplyReviewActionInput {
  kind: StaleReviewTargetKind;
  id: string;
  action: ReviewAction;
  suggestionId?: number;
  /** FRONT-2350: `update` only — the person's own version of the draft,
   *  sent only when it differs from EDDY's. */
  editedContent?: string;
  /** FRONT-3313: `update` on a passage-format draft — one entry per change. */
  decisions?: PassageDecision[];
}

/** The same cache prefixes an apply (and an undo of it) must refresh. */
function invalidateAfterReviewWrite(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: knowledgeReviewKeys.lists() });
  qc.invalidateQueries({ queryKey: feedKeys.all });
  // The sidebar's Knowledge badge reads the same count from /api/attention.
  qc.invalidateQueries({ queryKey: attentionKeys.all });
  qc.invalidateQueries({ queryKey: catalogueKeys.all });
  qc.invalidateQueries({ queryKey: documentKeys.all });
  qc.invalidateQueries({ queryKey: fileKeys.all });
  qc.invalidateQueries({ queryKey: knowledgeSearchKeys.all });
  // FRONT-2392: see use-files.ts's matching comment.
  qc.invalidateQueries({ queryKey: filesOverviewKeys.lists() });
}

/**
 * Apply the final decision for one item (update / delete / keep). See the
 * module doc comment for why every one of these prefixes gets
 * invalidated — an apply is the one moment every "may be out of date"
 * number on screen has to agree again.
 */
export function useApplyKnowledgeReviewAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ApplyReviewActionInput) =>
      api.post<{ ok: true; action: ReviewAction; edited?: boolean; undoable: boolean }>(
        '/api/knowledge-review/apply',
        input,
      ),
    onSuccess: () => {
      invalidateAfterReviewWrite(qc);
    },
  });
}

/**
 * FRONT-3313: put an applied document update back
 * (`POST /api/knowledge-review/undo`). Refreshes the same caches the apply
 * did, plus the chat panel's last-change lists, since the document's text
 * (and its history) just changed under them. The suggestion's own cached
 * entry is dropped too: undo re-opens the SAME draft id as `ready`.
 */
export function useUndoKnowledgeReviewUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { kind: 'document'; id: string; suggestionId: number }) =>
      api.post<{ ok: true; updatedAt: string }>('/api/knowledge-review/undo', input),
    onSuccess: (_res, vars) => {
      invalidateAfterReviewWrite(qc);
      qc.invalidateQueries({ queryKey: knowledgeReviewKeys.suggestion(vars.suggestionId) });
      qc.invalidateQueries({ queryKey: artifactChangeKeys.documentLastChanges() });
    },
  });
}
